import { createFileRoute, redirect } from '@tanstack/react-router';
import { AGGREGATE_NAMES, CORPORATION_RESOLVERS, USER_RESOLVERS, USER_ROLES } from 'cpool-constants';
import type { SchemaUserResolver_GetTeamsByCorporation } from 'cpool-tables/backend-resolvers';
import {
    schemaCorporationResolver_GetOwnCorporation,
    schemaUserResolver_GetDivisionsByUserId,
    schemaUserResolver_GetTeamsByCorporation,
} from 'cpool-tables/backend-resolvers';

import { authoriseUser } from '~/features/auth/utils/authoriseUser';
import { seedSpecificCache } from '~/features/userStructures/routes/utils/seedSpecificCache.ts';
import { getQueryOptions } from '~/queryUtils/getQueryOptions';
import { useUserStore } from '~/store/userStore';

const ownCorporationQueryOptions = () =>
    getQueryOptions({
        aggregateName: AGGREGATE_NAMES.CORPORATION,
        resolverName: CORPORATION_RESOLVERS.getOwnCorporation,
        schema: schemaCorporationResolver_GetOwnCorporation,
        searchParams: {},
    });

const teamsCorporationQueryOptions = () =>
    getQueryOptions({
        aggregateName: AGGREGATE_NAMES.USER,
        resolverName: USER_RESOLVERS.getDivisionsByUserId,
        schema: schemaUserResolver_GetDivisionsByUserId,
        searchParams: {},
    });

// keep the key shape of useTeamsByCorporation, otherwise this prefetch is never read
const teamsByCorporationQueryOptions = (corporationId?: string | null) =>
    getQueryOptions({
        aggregateName: AGGREGATE_NAMES.USER,
        resolverName: USER_RESOLVERS.getTeamsByCorporation,
        schema: schemaUserResolver_GetTeamsByCorporation,
        searchParams: { corporationId },
    });

export const Route = createFileRoute('/_app/management/divisions/')({
    beforeLoad: ({ location }) => {
        authoriseUser([USER_ROLES.corporation_director], location);
        const content = 'ROUTE_TITLE.DIVISION_MANAGEMENT';
        return {
            meta: [
                {
                    content,
                    name: 'description',
                },
                {
                    content,
                    name: 'title',
                },
            ],
        };
    },
    loader: async ({ context, location }) => {
        const { user } = useUserStore.getState();
        if (!user) {
            throw redirect({ search: { redirect: location.href }, to: '/login' });
        }

        const [, , teams] = await Promise.all([
            context.queryClient.ensureQueryData(ownCorporationQueryOptions()),
            context.queryClient.ensureQueryData(teamsCorporationQueryOptions()),
            context.queryClient.ensureQueryData(teamsByCorporationQueryOptions(user.corporationId)),
        ]);

        seedSpecificCache({
            queryClient: context.queryClient,
            teams: teams as SchemaUserResolver_GetTeamsByCorporation,
        });
    },
});
